import { Button, Form, Spinner } from 'react-bootstrap';
import { CustomerHMISParticipationCsv, VCOHMISParticipation, VCOProject } from './CustomProjectModels';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { Slide, toast } from 'react-toastify';

import { AddFilled } from '@carbon/icons-react';
import ApiHelper from '../../../Shared/ApiHelper';
import DatePicker from 'react-datepicker';
import DeleteButton from '../../../Shared/components/DeleteButton';
import SaveButton from '../../../Shared/components/SaveButton';
import Select from 'react-select';
import { SelectOption } from '../../../Shared/SharedModels';
import { UserProfileContext } from '../../../Shared/contexts/UserProfile';
import { useMsal } from '@azure/msal-react';

export interface HMISParticipationEditFormProps {
    vcoProject: VCOProject;
    batchKey: number;
}

function getParticipationOptionLabel(csv: CustomerHMISParticipationCsv): string {
    if (!csv.hmisParticipationID) return 'New Participation';

    return `Participation ID: ${csv.hmisParticipationID} - ${
        csv.hmisParticipationStatusEndDate ? 'Inactive' : 'Active'
    }`;
}

const HMISParticipationEditForm: FunctionComponent<HMISParticipationEditFormProps> = (props) => {
    const { vcoProject, batchKey } = props;
    const userProfile = useContext(UserProfileContext);

    const defaultParticipationCsv = useMemo<CustomerHMISParticipationCsv>(
        () => ({
            batchKey: 0,
            sourceSystemShortName: '',
            projectID: '',
        }),
        []
    );

    const [selectedParticipation, setSelectedParticipation] = useState<CustomerHMISParticipationCsv | undefined>();
    const [participationOptions, setParticipationOptions] = useState<SelectOption<CustomerHMISParticipationCsv>[]>([]);

    const [isSaving, setIsSaving] = useState(false);
    const [canSave, setCanSave] = useState(true);
    const [isLoading, setIsLoading] = useState(true);

    const api = useMemo(() => new ApiHelper(), []);
    const { instance } = useMsal();

    useEffect(() => {
        setCanSave(
            !!selectedParticipation?.hmisParticipationStatusStartDate && !!selectedParticipation.hmisParticipationType
        );
    }, [
        selectedParticipation?.projectID,
        selectedParticipation?.hmisParticipationStatusStartDate,
        selectedParticipation?.hmisParticipationType,
    ]);

    useEffect(() => {
        if (isLoading && vcoProject.projectKey && batchKey) {
            api.callApi(
                instance,
                [process.env.REACT_APP_B2C_SCOPE ?? ''],
                `${process.env.REACT_APP_NET_API_URL}/CustomProject/projects/${vcoProject.projectKey}/hmisParticipation`,
                'GET'
            )
                .then(async (result: Response) => {
                    const participations: VCOHMISParticipation[] = await result.json();
                    const customParticipations: CustomerHMISParticipationCsv[] =
                        participations.map<CustomerHMISParticipationCsv>((vco) => ({
                            batchKey: batchKey,
                            hmisParticipationID: vco.hmisParticipationID,
                            sourceSystemShortName:
                                userProfile.sourceSystems.find((s) => s.sourceSystemKey === vcoProject.sourceSystemKey)
                                    ?.sourceSystemShortName ?? '',
                            projectID: vco.projectID ?? '',
                            hmisParticipationType: vco.hmisParticipationTypeKey?.toString() ?? '',
                            hmisParticipationStatusStartDate: vco.hmisParticipationStatusStartDate,
                            hmisParticipationStatusEndDate: vco.hmisParticipationStatusEndDate,
                            dateCreated: vco.dateCreated,
                            dateUpdated: vco.dateUpdated,
                            userID: vco.userID,
                            dateDeleted: vco.dateDeleted,
                            batchGUID: undefined,
                        }));

                    if (customParticipations.length) {
                        setParticipationOptions(
                            customParticipations
                                .map((csv) => ({
                                    label: getParticipationOptionLabel(csv),
                                    value: csv,
                                }))
                                .sort((a, b) => {
                                    if (
                                        a.value.hmisParticipationStatusEndDate &&
                                        b.value.hmisParticipationStatusEndDate
                                    ) {
                                        return 0;
                                    } else if (a.value.hmisParticipationStatusEndDate) {
                                        return 1;
                                    } else {
                                        return -1;
                                    }
                                })
                        );

                        setSelectedParticipation(customParticipations[0]);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [
        api,
        batchKey,
        instance,
        isLoading,
        userProfile.sourceSystems,
        vcoProject.projectKey,
        vcoProject.sourceSystemKey,
    ]);

    return (
        <div className="project-card mb-5">
            <div className="project-card-header">
                <h3>HMISParticipation CSV Data</h3>

                {!!batchKey && !isLoading && (
                    <div className="flex-row">
                        {selectedParticipation?.hmisParticipationID && (
                            <DeleteButton
                                isLoading={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const dateDeleted = new Date().toISOString();

                                    const payload: CustomerHMISParticipationCsv = {
                                        ...selectedParticipation,
                                        dateDeleted: dateDeleted,
                                        dateUpdated: dateDeleted,
                                        hmisParticipationStatusStartDate:
                                            selectedParticipation.hmisParticipationStatusStartDate?.split('T')[0],
                                        hmisParticipationStatusEndDate:
                                            selectedParticipation.hmisParticipationStatusEndDate?.split('T')[0],
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        `${process.env.REACT_APP_NET_API_URL}/CustomProject/hmisParticipation`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Participation Data Deleted Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}

                        <Button
                            className="form-action-button mr-2 ml-2"
                            variant="outline-primary"
                            disabled={isSaving}
                            onClick={() => {
                                const currentDate = new Date().toISOString().split('T')[0];
                                const newParticipation: CustomerHMISParticipationCsv = {
                                    batchKey: batchKey,
                                    sourceSystemShortName:
                                        userProfile.sourceSystems.find(
                                            (s) => s.sourceSystemKey === vcoProject.sourceSystemKey
                                        )?.sourceSystemShortName ?? '',
                                    projectID: vcoProject.projectId ?? '',
                                    dateCreated: currentDate,
                                    dateUpdated: currentDate,
                                    userID: vcoProject.userID,
                                };

                                setSelectedParticipation(newParticipation);
                            }}
                        >
                            <AddFilled size={24} className="button-icon-padding" /> Add Participation
                        </Button>

                        {selectedParticipation && (
                            <SaveButton
                                isSaving={isSaving}
                                isDisabled={!canSave}
                                onClick={() => {
                                    setIsSaving(true);
                                    const payload: CustomerHMISParticipationCsv = {
                                        ...selectedParticipation,
                                        hmisParticipationStatusStartDate:
                                            selectedParticipation.hmisParticipationStatusStartDate?.split('T')[0],
                                        hmisParticipationStatusEndDate:
                                            selectedParticipation.hmisParticipationStatusEndDate?.split('T')[0],
                                    };

                                    api.callApi(
                                        instance,
                                        [process.env.REACT_APP_B2C_SCOPE ?? ''],
                                        `${process.env.REACT_APP_NET_API_URL}/CustomProject/hmisParticipation`,
                                        'POST',
                                        JSON.stringify(payload)
                                    )
                                        .then(async (_result: Response) => {
                                            toast.success('Participation Data Saved Successfully', {
                                                position: 'top-right',
                                                autoClose: 5000,
                                                transition: Slide,
                                                draggable: false,
                                                closeOnClick: false,
                                                theme: 'colored',
                                            });

                                            setIsLoading(true);
                                        })
                                        .finally(() => {
                                            setIsSaving(false);
                                        });
                                }}
                            />
                        )}
                    </div>
                )}
            </div>

            {isLoading ? (
                <Spinner
                    className="centered-spinner"
                    variant="primary"
                    size="sm"
                    animation="border"
                    data-testid="participationEditFormSpinner"
                />
            ) : (
                <div className="project-card-body">
                    {participationOptions.length || selectedParticipation ? (
                        <>
                            <Form.Group className="wide-card-form-group">
                                <Form.Label className={isSaving ? 'disabled-label' : ''}>Participation</Form.Label>
                                <Select
                                    isClearable
                                    isLoading={isLoading}
                                    isDisabled={isSaving}
                                    className="filterSelect"
                                    placeholder="Select Participation..."
                                    value={
                                        selectedParticipation
                                            ? {
                                                  label: getParticipationOptionLabel(selectedParticipation),
                                                  value: selectedParticipation,
                                              }
                                            : undefined
                                    }
                                    options={participationOptions}
                                    onChange={(selectedOption) => {
                                        setSelectedParticipation(selectedOption?.value);
                                    }}
                                />
                            </Form.Group>

                            <Form className="card-form">
                                {selectedParticipation && (
                                    <>
                                        <Form.Group className="card-form-group">
                                            <Form.Label>Participation Type*</Form.Label>
                                            <Form.Control
                                                data-testid="participationTypeInput"
                                                placeholder="Enter Participation Type"
                                                value={selectedParticipation.hmisParticipationType ?? ''}
                                                isInvalid={!selectedParticipation.hmisParticipationType}
                                                onChange={(event) => {
                                                    const numberValue = parseInt(event.target.value);
                                                    const isNumber = !isNaN(numberValue);

                                                    if (isNumber) {
                                                        setSelectedParticipation((prev) => ({
                                                            ...defaultParticipationCsv,
                                                            ...prev,
                                                            hmisParticipationType: numberValue.toString(),
                                                        }));
                                                    } else if (event.target.value === '') {
                                                        setSelectedParticipation((prev) => ({
                                                            ...defaultParticipationCsv,
                                                            ...prev,
                                                            hmisParticipationType: undefined,
                                                        }));
                                                    }
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>Start Date*</Form.Label>
                                            <DatePicker
                                                placeholderText="Enter Start Date"
                                                className="form-control"
                                                dateFormat="MM-dd-yyyy"
                                                selected={
                                                    selectedParticipation.hmisParticipationStatusStartDate
                                                        ? new Date(
                                                              selectedParticipation.hmisParticipationStatusStartDate
                                                          )
                                                        : undefined
                                                }
                                                onChange={(date: Date | null) => {
                                                    setSelectedParticipation((prev) => ({
                                                        ...defaultParticipationCsv,
                                                        ...prev,
                                                        hmisParticipationStatusStartDate: date
                                                            ? date.toISOString()
                                                            : undefined,
                                                    }));
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="card-form-group">
                                            <Form.Label>End Date</Form.Label>
                                            <DatePicker
                                                placeholderText="Enter End Date"
                                                className="form-control"
                                                dateFormat="MM-dd-yyyy"
                                                selected={
                                                    selectedParticipation.hmisParticipationStatusEndDate
                                                        ? new Date(selectedParticipation.hmisParticipationStatusEndDate)
                                                        : undefined
                                                }
                                                onChange={(date: Date | null) => {
                                                    setSelectedParticipation((prev) => ({
                                                        ...defaultParticipationCsv,
                                                        ...prev,
                                                        hmisParticipationStatusEndDate: date
                                                            ? date.toISOString()
                                                            : undefined,
                                                    }));
                                                }}
                                            />
                                        </Form.Group>
                                    </>
                                )}
                            </Form>
                        </>
                    ) : (
                        <p>No participation records found</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default HMISParticipationEditForm;
